import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Icon } from "../ui/Icon";

/**
 * Minimal toast/notification system (replaces the legacy `showAlert`). Styled
 * with the existing design tokens — not a second design system. Auto-dismiss
 * matches the legacy ~5s; a persistent variant is available for partial-failure
 * summaries (duration 0).
 */

export type ToastKind = "success" | "error" | "warning" | "info";

/**
 * One recovery the toast can offer.
 *
 * The canvas draws its error toast as "Couldn't delete the chat · Retry": when
 * the failure has exactly one obvious next move, the toast carries it, rather
 * than telling the user something went wrong and leaving them to find the
 * gesture again themselves.
 */
export interface ToastAction {
  label: string;
  onAction: () => void;
}

export interface ToastOptions {
  durationMs?: number;
  action?: ToastAction;
}

interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
  action?: ToastAction;
}

/** `number` is the duration, kept so existing callers read the same. */
type ToastArg = number | ToastOptions;

interface ToastApi {
  success: (msg: string, options?: ToastArg) => void;
  error: (msg: string, options?: ToastArg) => void;
  warning: (msg: string, options?: ToastArg) => void;
  info: (msg: string, options?: ToastArg) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const DEFAULT_DURATION = 5000;

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (kind: ToastKind, message: string, arg: ToastArg | undefined, fallback: number) => {
      const options: ToastOptions = typeof arg === "number" ? { durationMs: arg } : (arg ?? {});
      const durationMs = options.durationMs ?? fallback;
      const id = nextId.current++;
      setToasts((list) => [...list, { id, kind, message, action: options.action }]);
      if (durationMs > 0) {
        setTimeout(() => dismiss(id), durationMs);
      }
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: (m, o) => push("success", m, o, DEFAULT_DURATION),
      // An error stays until it is read or acted on.
      error: (m, o) => push("error", m, o, 0),
      warning: (m, o) => push("warning", m, o, DEFAULT_DURATION),
      info: (m, o) => push("info", m, o, DEFAULT_DURATION),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toast-stack" role="region" aria-label="Notifications">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`toast toast-${t.kind}`}
            role={t.kind === "error" ? "alert" : "status"}
          >
            <Icon name={t.kind === "success" ? "check" : "alertCircle"} size={15} />
            <span className="toast-message">{t.message}</span>
            {t.action && (
              <button
                type="button"
                className="toast-action"
                onClick={() => {
                  dismiss(t.id);
                  t.action?.onAction();
                }}
              >
                {t.action.label}
              </button>
            )}
            <button
              type="button"
              className="toast-close"
              aria-label="Dismiss"
              onClick={() => dismiss(t.id)}
            >
              <Icon name="close" size={11} stroke={2.6} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within a ToastProvider");
  return ctx;
}
